/**
 * Pure fetch for Admin Dashboard — used by useAdminDashboardData (React Query).
 * Keeps dashboard lists in TanStack Query cache so navigation back from sub-routes is instant.
 */
import { supabase, executeWithRetry } from "@/lib/supabase";
import { CLEANING_STAFF_ROLES, isCleaningStaffRole } from "@/lib/cleaningStaffRoles";
import { isAssignmentPlaceholderSection } from "@/lib/locationAccess";
import { filterTasksForDisplay, type TaskFilterContext } from "@/lib/task-filters";
import { getTasksForCurrentView, getEffectiveTaskStatus } from "@/lib/task-progress";
import type { SnowSessionRow } from "@/lib/snowMapStatus";
import type { MapLocation, MarkerStatus } from "@/components/admin/AdminMap";
import type { CriticalAlert } from "@/components/admin/AdminDashboardAlerts";
import type { RecentActivityItem } from "@/components/admin/AdminDashboardActivity";
import type { DayStats } from "@/components/admin/AdminDashboardStats";
import type { SearchableSelectOption } from "@/components/admin/SearchableSelect";

const DASHBOARD_ACTIVITY_PAGE_SIZE = 10;

type TaskRow = {
  id: string;
  location_id: string | null;
  status: string | null;
  task_type: string | null;
  scheduled_at: string;
  assigned_staff_id: string | null;
  actual_performer_id: string | null;
  section_id: string | null;
  coordinator_notes: string | null;
  completed_at: string | null;
  check_in_metadata: { started_by?: string; resumed_by?: string; timestamp?: string } | null;
  checklist?: unknown;
};

function startOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export type AdminDashboardSnapshot = {
  stats: DayStats;
  mapLocations: MapLocation[];
  propertyOptions: SearchableSelectOption[];
  workerOptions: SearchableSelectOption[];
  alerts: CriticalAlert[];
  activity: RecentActivityItem[];
  activityHasMore: boolean;
  activityPage: number;
  locations: { id: string; address: string }[];
  sectionsByLocation: Record<
    string,
    { id: string; name: string; assigned_staff_id: string | null }[]
  >;
  locationsWithoutCoords: { id: string; address: string }[];
  globalCoordinatorViewFinancials: boolean;
  isSnowSeason: boolean;
  taskGenerationDays: number;
};

export async function fetchAdminDashboardSnapshot(
  currentOrgId: string,
  showArchivedProperties: boolean
): Promise<AdminDashboardSnapshot> {
  return executeWithRetry(async () => {
    const filterContext: TaskFilterContext = {
      todayStart: startOfTodayLocal(),
      todayEnd: endOfTodayLocal(),
    };

    const locQuery = supabase
      .from("cleaning_locations")
      .select("id, address, latitude, longitude, status")
      .eq("org_id", currentOrgId)
      .eq("is_cleaning_active", true);

    if (!showArchivedProperties) {
      locQuery.eq("status", "active");
    }

    const locRes = await locQuery;
    if (locRes.error) {
      console.error("[AdminDashboard] Błąd pobierania cleaning_locations:", locRes.error);
    }
    const locs = (locRes.data ?? []) as {
      id: string;
      address: string;
      latitude: number | null;
      longitude: number | null;
      status: string | null;
    }[];
    console.log(
      `[AdminDashboard] Pobrano ${locs.length} nieruchomości (org_id=${currentOrgId}, showArchived=${showArchivedProperties})`
    );

    const activeLocs = locs.filter((l) => l.status === "active" || l.status === null);
    const locsForMap = showArchivedProperties ? locs : activeLocs;
    const locIds = locsForMap.map((l) => l.id);

    const sevenDaysAgo = new Date(filterContext.todayStart);
    sevenDaysAgo.setUTCDate(sevenDaysAgo.getUTCDate() - 7);
    const thirtyDaysAhead = new Date(filterContext.todayEnd);
    thirtyDaysAhead.setUTCDate(thirtyDaysAhead.getUTCDate() + 31);
    const sevenDaysAgoISO = sevenDaysAgo.toISOString();
    const thirtyDaysAheadISO = thirtyDaysAhead.toISOString();

    const [tasksRes, activityLogsRes, workersRes, sectionsRes, orgRes] = await Promise.all([
      supabase
        .from("cleaning_tasks")
        .select(
          "id, location_id, status, task_type, scheduled_at, assigned_staff_id, actual_performer_id, section_id, coordinator_notes, completed_at, check_in_metadata, checklist"
        )
        .eq("org_id", currentOrgId)
        .in("task_type", ["sop_standard", "coordinator_single", "employee_extra", "long_term"])
        .or(
          `and(scheduled_at.gte.${sevenDaysAgoISO},scheduled_at.lte.${thirtyDaysAheadISO}),task_type.eq.coordinator_single,task_type.eq.employee_extra`
        ),
      supabase
        .from("task_step_logs")
        .select("id, user_id, step_name, created_at, cleaning_tasks!inner(location_id, org_id)")
        .eq("cleaning_tasks.org_id", currentOrgId)
        .order("created_at", { ascending: false })
        .range(0, DASHBOARD_ACTIVITY_PAGE_SIZE - 1),
      supabase.from("memberships").select("user_id").eq("org_id", currentOrgId).in("role", [...CLEANING_STAFF_ROLES]).eq("is_active", true),
      locIds.length > 0
        ? supabase
            .from("property_sections")
            .select("id, location_id, name, assigned_staff_id")
            .in("location_id", locIds)
            .eq("is_active", true)
        : Promise.resolve({ data: [] }),
      supabase
        .from("organizations")
        .select("global_coordinator_view_financials, is_snow_season, task_generation_days")
        .eq("id", currentOrgId)
        .single(),
    ]);

    const tasks = (tasksRes.data ?? []) as TaskRow[];
    const globalFiltered = filterTasksForDisplay(tasks, filterContext);

    const taskIds = [
      ...new Set(tasks.filter((t) => t.location_id && locIds.includes(t.location_id)).map((t) => t.id)),
    ];
    let executionLogs: { task_id: string; user_id: string; action_type: string; created_at: string }[] =
      [];
    if (taskIds.length > 0) {
      const { data: logs } = await supabase
        .from("task_execution_logs")
        .select("task_id, user_id, action_type, created_at")
        .in("task_id", taskIds)
        .order("created_at", { ascending: true });
      executionLogs = (logs ?? []) as typeof executionLogs;
    }
    const taskIdToLocationId = new Map(
      tasks.filter((t) => t.location_id).map((t) => [t.id, t.location_id as string])
    );
    const locationToCheckedInUserId = new Map<string, string>();
    const checkInsByTask = new Map<string, { userId: string; at: string }[]>();
    const checkOutsByTask = new Map<string, string[]>();
    for (const log of executionLogs) {
      if (!log.task_id || !log.user_id) continue;
      if (log.action_type === "CHECK_IN") {
        const arr = checkInsByTask.get(log.task_id) ?? [];
        arr.push({ userId: log.user_id, at: log.created_at ?? "" });
        checkInsByTask.set(log.task_id, arr);
      } else if (log.action_type === "CHECK_OUT") {
        const arr = checkOutsByTask.get(log.task_id) ?? [];
        arr.push(log.created_at ?? "");
        checkOutsByTask.set(log.task_id, arr);
      }
    }
    for (const [taskId, checkIns] of checkInsByTask) {
      const locationId = taskIdToLocationId.get(taskId);
      if (!locationId || !locIds.includes(locationId)) continue;
      const checkOuts = checkOutsByTask.get(taskId) ?? [];
      const lastCheckIn = checkIns[checkIns.length - 1];
      const lastCheckOut = checkOuts[checkOuts.length - 1];
      if (!lastCheckOut || lastCheckIn.at > lastCheckOut) {
        locationToCheckedInUserId.set(locationId, lastCheckIn.userId);
      }
    }
    const checkedInUserIds = [...new Set(locationToCheckedInUserId.values())];
    let presenceProfiles: { id: string; full_name: string | null }[] = [];
    if (checkedInUserIds.length > 0) {
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", checkedInUserIds);
      presenceProfiles = (prof ?? []) as typeof presenceProfiles;
    }
    const presenceNameById = new Map(
      presenceProfiles.map((p) => [p.id, p.full_name ?? p.id.slice(0, 8)])
    );

    type ActivityLogRow = {
      id: string;
      user_id: string;
      step_name: string;
      created_at: string;
      cleaning_tasks: { location_id: string | null; org_id: string } | null;
    };
    const activityLogs = (activityLogsRes.data ?? []) as ActivityLogRow[];

    const workersData = (workersRes.data ?? []) as { user_id: string }[];
    const uniqueWorkerIds = new Set(workersData.map((w) => w.user_id).filter(Boolean));
    const workersCount = uniqueWorkerIds.size;
    console.log(
      `[AdminDashboard] Liczba pracowników (unikalne user_id, role Cleaning): ${workersCount}`
    );

    const cleanerIds: string[] = [];
    const sections = ((sectionsRes.data ?? []) as {
      id: string;
      location_id: string | null;
      name: string;
      assigned_staff_id: string | null;
    }[]).filter((s) => !isAssignmentPlaceholderSection(s.name));

    const sectionById = new Map<string, string>();
    for (const s of sections) {
      if (s.id) sectionById.set(s.id, s.name);
    }

    function taskDisplayName(t: TaskRow): string {
      const sec = t.section_id ? sectionById.get(t.section_id) : null;
      if (sec) return sec;
      if (t.coordinator_notes?.trim()) return t.coordinator_notes.trim();
      switch (t.task_type) {
        case "coordinator_single":
          return "Zadanie koordynatora";
        case "long_term":
          return "Zadanie długoterminowe";
        case "employee_extra":
          return "Zadanie dodatkowe";
        default:
          return "Zadanie standardowe";
      }
    }

    const mapLocs: MapLocation[] = locsForMap.map((l) => {
      const locationTasks = tasks.filter((t) => t.location_id === l.id);
      const tasksByView = filterTasksForDisplay(locationTasks, filterContext);

      const baseTasks = getTasksForCurrentView(locationTasks, filterContext);
      const futureExtra = (tasksByView.future ?? []).filter(
        (t) => t.task_type === "coordinator_single" || t.task_type === "employee_extra"
      );
      const validTasksForToday = [...baseTasks, ...futureExtra];

      const totalTasks = validTasksForToday.length;
      const completedTasks = validTasksForToday.filter((t) => getEffectiveTaskStatus(t) === "done").length;

      const hasOverdue = tasksByView.overdue.length > 0;
      const onlyFuture =
        tasksByView.overdue.length === 0 &&
        tasksByView.todayPending.length === 0 &&
        tasksByView.todayDone.length === 0 &&
        tasksByView.future.length > 0;

      let status: MarkerStatus;
      if (hasOverdue) {
        status = "red";
      } else if (onlyFuture) {
        status = "hidden";
      } else if (totalTasks === 0) {
        status = "gray";
      } else if (totalTasks === completedTasks) {
        status = "green";
      } else {
        status = "yellow";
      }

      const checkedInUserId = locationToCheckedInUserId.get(l.id);
      const currentWorkerName = checkedInUserId ? presenceNameById.get(checkedInUserId) ?? null : null;

      const pending = validTasksForToday
        .filter((t) => getEffectiveTaskStatus(t) !== "done")
        .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
      const nextTask = pending[0] ?? null;
      const nextTaskName = nextTask ? taskDisplayName(nextTask) : null;

      return {
        id: l.id,
        address: l.address,
        latitude: l.latitude,
        longitude: l.longitude,
        status,
        currentWorkerName: currentWorkerName ?? null,
        nextTaskName,
        taskProgress: totalTasks > 0 ? { total: totalTasks, completed: completedTasks } : null,
      };
    });

    const withoutCoords = mapLocs.filter((l) => {
      const lat = l.latitude;
      const lng = l.longitude;
      if (lat == null || lng == null || typeof lat !== "number" || typeof lng !== "number") return true;
      if (Number.isNaN(lat) || Number.isNaN(lng)) return true;
      if (lat === 0 && lng === 0) return true;
      return false;
    });
    if (withoutCoords.length > 0) {
      console.warn(
        "[AdminDashboard] Nieruchomości bez lokalizacji GPS (nie wyświetlają się na mapie):",
        withoutCoords.map((l) => ({ id: l.id, address: l.address }))
      );
    }
    const statusCounts = mapLocs.reduce(
      (acc, l) => {
        acc[l.status] = (acc[l.status] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
    console.log(
      `[AdminDashboard] Mapa: ${mapLocs.length - withoutCoords.length} z ${mapLocs.length} obiektów ma współrzędne. Statusy:`,
      statusCounts
    );

    const notStartedBuildings = mapLocs.filter((l) => l.status === "red");
    const inProgressBuildings = mapLocs.filter((l) => l.status === "yellow");
    const doneBuildings = mapLocs.filter((l) => l.status === "green");
    const excludedBuildings = mapLocs.filter((l) => l.status === "gray" || l.status === "hidden");

    const notStarted = notStartedBuildings.length;
    const inProgressCount = inProgressBuildings.length;
    const doneCount = doneBuildings.length;

    console.log("[AdminDashboard] Stats (budynki) - Jeszcze nie zaczęli (red):", {
      count: notStarted,
      addresses: notStartedBuildings.map((l) => l.address),
    });
    console.log("[AdminDashboard] Stats (budynki) - W trakcie (yellow):", {
      count: inProgressCount,
      addresses: inProgressBuildings.map((l) => l.address),
    });
    console.log("[AdminDashboard] Stats (budynki) - Zakończyli (green):", {
      count: doneCount,
      addresses: doneBuildings.map((l) => l.address),
    });
    console.log("[AdminDashboard] Stats (budynki) - Pominięte (gray+hidden):", {
      count: excludedBuildings.length,
      addresses: excludedBuildings.map((l) => l.address),
    });

    const propertyOptions: SearchableSelectOption[] = locs.map((l) => ({
      value: l.id,
      label: l.status === "archived" ? `${l.address} (Zarchiwizowana)` : l.address,
    }));
    const locations = locs.map((l) => ({ id: l.id, address: l.address }));

    const secByLoc: Record<
      string,
      { id: string; name: string; assigned_staff_id: string | null }[]
    > = {};
    for (const s of sections) {
      const lid = s.location_id ?? "_";
      if (!secByLoc[lid]) secByLoc[lid] = [];
      secByLoc[lid].push({
        id: s.id,
        name: s.name,
        assigned_staff_id: s.assigned_staff_id,
      });
    }

    const allStaffIds = [...new Set(cleanerIds)];
    const activityStaffIds = [...new Set(activityLogs.map((log) => log.user_id))];
    const idsToFetch = [...new Set([...allStaffIds, ...activityStaffIds])];
    let profiles: { id: string; full_name: string | null }[] = [];
    if (idsToFetch.length > 0) {
      const { data: prof } = await supabase.from("profiles").select("id, full_name").in("id", idsToFetch);
      profiles = (prof ?? []) as { id: string; full_name: string | null }[];
    }

    console.log("Pobieranie ID pracowników z memberships dla:", currentOrgId);

    const { data: membersData, error: membersError } = await supabase
      .from("memberships")
      .select("user_id, role, is_active")
      .eq("org_id", currentOrgId)
      .in("role", [...CLEANING_STAFF_ROLES]);

    let workerOptions: SearchableSelectOption[] = [];
    if (membersError) {
      console.error("Błąd pobierania memberships:", membersError);
    } else {
      const userIds = Array.from(
        new Set(
          (membersData || [])
            .filter((m) => m.user_id && isCleaningStaffRole(m.role))
            .filter((m) => showArchivedProperties || m.is_active !== false)
            .map((m) => m.user_id)
            .filter((id) => id)
        )
      );

      if (userIds.length > 0) {
        console.log("Pobieranie profili dla ID:", userIds);

        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);

        if (profilesError) {
          console.error("Błąd pobierania profiles:", profilesError);
        } else {
          const archivedIds = new Set(
            (membersData || [])
              .filter((m) => m.is_active === false)
              .map((m) => m.user_id)
          );
          workerOptions = (profilesData || []).map((p) => ({
            value: p.id,
            label: archivedIds.has(p.id)
              ? `${p.full_name || p.email || "Użytkownik bez nazwy"} (zarchiwizowany)`
              : p.full_name || p.email || "Użytkownik bez nazwy",
          }));
          console.log("Gotowa lista pracowników:", workerOptions);
        }
      } else {
        console.log("Brak pracowników w tej organizacji.");
      }
    }

    const locMap = new Map(locsForMap.map((l) => [l.id, l.address]));

    const alertsWithAddr: CriticalAlert[] = globalFiltered.overdue.map((a) => ({
      id: a.id,
      location_id: a.location_id ?? "",
      location_address: a.location_id ? locMap.get(a.location_id) ?? "?" : "?",
      section_name: a.section_id ? sectionById.get(a.section_id) ?? null : null,
      scheduled_at: a.scheduled_at,
      coordinator_notes: a.coordinator_notes,
    }));

    const activityWithDetails: RecentActivityItem[] = [];
    for (const log of activityLogs) {
      const task = log.cleaning_tasks;
      const locationId = task?.location_id ?? null;
      const addr = locationId ? locMap.get(locationId) ?? "?" : "?";
      const p = profiles.find((x) => x.id === log.user_id);
      const staffName = p?.full_name ?? null;
      activityWithDetails.push({
        id: log.id,
        completed_at: log.created_at,
        staff_name: staffName,
        staff_id: log.user_id,
        location_address: addr,
        location_id: locationId ?? "",
        step_name: log.step_name,
      });
    }

    const orgData = orgRes.data as {
      global_coordinator_view_financials?: boolean | null;
      is_snow_season?: boolean | null;
      task_generation_days?: number | null;
    } | null;

    return {
      stats: { notStarted, inProgress: inProgressCount, done: doneCount },
      mapLocations: mapLocs,
      propertyOptions,
      workerOptions,
      alerts: alertsWithAddr,
      activity: activityWithDetails,
      activityHasMore: activityLogs.length >= DASHBOARD_ACTIVITY_PAGE_SIZE,
      activityPage: 0,
      locations,
      sectionsByLocation: secByLoc,
      locationsWithoutCoords: withoutCoords.map((l) => ({ id: l.id, address: l.address })),
      globalCoordinatorViewFinancials: orgData?.global_coordinator_view_financials ?? false,
      isSnowSeason: orgData?.is_snow_season ?? false,
      taskGenerationDays: orgData?.task_generation_days ?? 15,
    };
  }, { isReadOperation: true });
}

export type SnowMapSessionsResult = {
  sessions: SnowSessionRow[];
  workerNameById: Record<string, string>;
};

export async function fetchSnowMapSessions(
  orgId: string,
  cutoff: Date
): Promise<SnowMapSessionsResult> {
  return executeWithRetry(async () => {
    const cutoffIso = cutoff.toISOString();
    const base = () =>
      supabase
        .from("cleaning_tasks")
        .select(
          "id, location_id, status, started_at, completed_at, assigned_staff_id, check_in_metadata, task_type"
        )
        .eq("org_id", orgId)
        .contains("check_in_metadata", { session_type: "snow_removal" });

    const [doneRes, inProgressRes] = await Promise.all([
      base().eq("status", "done").gte("completed_at", cutoffIso),
      base().eq("status", "in_progress"),
    ]);

    if (doneRes.error) {
      console.error("[AdminDashboard] Błąd pobierania sesji odśnieżania:", doneRes.error);
      throw doneRes.error;
    }
    if (inProgressRes.error) {
      console.error("[AdminDashboard] Błąd pobierania sesji odśnieżania w toku:", inProgressRes.error);
      throw inProgressRes.error;
    }

    const sessions = [
      ...((doneRes.data ?? []) as SnowSessionRow[]),
      ...((inProgressRes.data ?? []) as SnowSessionRow[]),
    ];
    const workerIds = [
      ...new Set(
        sessions
          .filter((s) => s.status === "in_progress" && s.assigned_staff_id)
          .map((s) => s.assigned_staff_id as string)
      ),
    ];

    let workerNameById: Record<string, string> = {};
    if (workerIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", workerIds);
      if (profilesError) {
        console.error("[AdminDashboard] Błąd pobierania pracowników odśnieżania:", profilesError);
      } else {
        workerNameById = Object.fromEntries(
          ((profiles ?? []) as { id: string; full_name: string | null }[]).map((p) => [
            p.id,
            p.full_name ?? p.id.slice(0, 8),
          ])
        );
      }
    }

    return { sessions, workerNameById };
  }, { isReadOperation: true });
}

export { DASHBOARD_ACTIVITY_PAGE_SIZE };
